import * as React from 'react'

import { IncidencesTabsDocument, PolicyTabsDocument } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { NavigationRoutesFn } from 'modules/core/lib/react-router'

const SelfServicePage = React.lazy(() => import('modules/payroll_bundle/pages/SelfServicePage'))

const IncidenceNoteTab = React.lazy(
  () => import('modules/bookkeepers/pages/incidences/IncidenceNoteTab')
)
const CyclesPage = React.lazy(() => import('modules/payroll_bundle/pages/CyclesPage'))
const GuardedIncidencesPage = React.lazy(
  () => import('modules/payroll_bundle/pages/GuardedIncidencesPage')
)
const IncidenceDetailPage = React.lazy(
  () => import('modules/payroll_bundle/pages/IncidenceDetailPage')
)
const IncidenceHistoryPage = React.lazy(
  () => import('modules/payroll_bundle/pages/IncidenceHistoryPage')
)
const IncidenceMessagePage = React.lazy(
  () => import('modules/payroll_bundle/pages/IncidenceMessagePage')
)
const InhouseActivationPage = React.lazy(
  () => import('modules/payroll_bundle/pages/InhouseActivationPage')
)
const NoPoliciesPage = React.lazy(() => import('modules/payroll_bundle/pages/NoPoliciesPage'))
const OffCyclesPage = React.lazy(() => import('modules/payroll_bundle/pages/OffCyclesPage'))
const PayrollIssuesNewLayoutPage = React.lazy(
  () => import('modules/payroll_bundle/pages/PayrollIssuesPage')
)
const PayrollPaywallGuard = React.lazy(
  () => import('modules/payroll_bundle/pages/PayrollPaywallGuard')
)
const PolicyPage = React.lazy(() => import('modules/payroll_bundle/pages/PolicyPage'))
const PolicyPeriodDashboardPage = React.lazy(
  () => import('modules/payroll_bundle/pages/PolicyPeriodDashboardPage')
)

const RedirectCyclesPage = React.lazy(
  () => import('modules/payroll_bundle/pages/RedirectCyclesPage')
)
const RedirectResultsPage = React.lazy(
  () => import('modules/payroll_bundle/pages/RedirectResultsPage')
)
const RedirectSupplementsPage = React.lazy(
  () => import('modules/payroll_bundle/pages/RedirectSupplementsPage')
)
const PaymentsPage = React.lazy(
  () => import('modules/payroll_bundle/pages/ResultsAndPaymentsPage/PaymentsPage')
)
const ResultsPage = React.lazy(
  () => import('modules/payroll_bundle/pages/ResultsAndPaymentsPage/ResultsPage')
)
const RootPage = React.lazy(() => import('modules/payroll_bundle/pages/RootPage'))
const SupplementsPage = React.lazy(() => import('modules/payroll_bundle/pages/SupplementsPage'))

const GeneratePaymentsPage = React.lazy(
  () =>
    import(
      'modules/payroll_bundle/pages/ResultsAndPaymentsPage/PaymentsPage/GeneratePaymentsPage/index'
    )
)

const BulkImportPage = React.lazy(
  () => import('modules/payroll_bundle/pages/SupplementsPage/BulkImport')
)

export const payrollBundleRoutes: NavigationRoutesFn = (navigation) => [
  {
    path: navigation.payrollBundle.path,
    element: <RootPage />,
    handle: {
      module: 'payroll_bundle',
      meta: ({ watchQuery }) => {
        return watchQuery({
          query: IncidencesTabsDocument,
        }).map(({ data }) => {
          return {
            title: i18n.t('payroll_bundle.title'),
            settings: data?.payroll._policies.managePayrollIncidences.can
              ? {
                  items: [
                    {
                      label: i18n.t('payroll_bundle.processing.title'),
                      href: navigation.settings.payroll_bundle.fullPath(),
                    },
                    {
                      label: i18n.t('payroll_bundle.updates.title'),
                      href: navigation.settings.payroll_bundle.incidences.fullPath(),
                    },
                  ],
                }
              : undefined,
            description: i18n.t('payroll_bundle.payroll_issues.description'),
          }
        })
      },
    },
    children: [
      {
        index: true,
        element: <RedirectCyclesPage />,
      },
      {
        path: navigation.payrollBundle.noPolicies.path,
        element: <NoPoliciesPage />,
      },
      {
        path: navigation.payrollBundle.setup.path,
        element: <SelfServicePage />,
      },
      {
        path: navigation.payrollBundle.results.path,
        element: <RedirectResultsPage />,
      },
      {
        path: navigation.payrollBundle.supplements.path,
        element: <RedirectSupplementsPage />,
      },
      {
        path: navigation.payrollBundle.incidences.path,
        element: <GuardedIncidencesPage />,
        handle: {
          meta: ({ watchQuery }) => {
            return watchQuery({
              query: IncidencesTabsDocument,
            }).map(({ data }) => {
              return {
                title: i18n.t('payroll_bundle.updates.title'),
                subNavigation: [
                  {
                    index: true,
                    title: i18n.t('payroll_bundle.processing.title'),
                    href: navigation.payrollBundle.fullPath(),
                    isVisible: data?.payroll._policies.sidebarSeePayrollBundleCycleSubLevelItem.can,
                  },
                  {
                    title: i18n.t('payroll_bundle.updates.title'),
                    href: navigation.payrollBundle.incidences.fullPath(),
                    isVisible:
                      data?.payroll._policies.sidebarSeePayrollBundleIncidencesSubLevelItem.can,
                  },
                  {
                    title: i18n.t('payroll_bundle.inhouse_activation.title'),
                    href: navigation.payrollBundle.inhouseActivation.path,
                    isVisible:
                      data?.payroll._policies.sidebarSeePayrollBundleInHouseActivationSubLevelItem
                        .can,
                  },
                ],
              }
            })
          },
        },
        children: [
          {
            index: true,
            path: navigation.payrollBundle.incidences.id.path,
            element: <IncidenceDetailPage />,
          },
          {
            path: navigation.payrollBundle.incidences.idChanges.path,
            element: <IncidenceHistoryPage />,
          },
          {
            path: navigation.payrollBundle.incidences.idMessages.path,
            element: <IncidenceMessagePage />,
          },
          {
            path: navigation.payrollBundle.incidences.idNotes.path,
            element: <IncidenceNoteTab />,
          },
        ],
      },
      {
        path: navigation.payrollBundle.inhouseActivation.path,
        element: <InhouseActivationPage />,
      },
      {
        path: navigation.payrollBundle.policy.path,
        element: (
          <PayrollPaywallGuard>
            <PolicyPage />
          </PayrollPaywallGuard>
        ),
        handle: {
          meta: ({ watchQuery, params }) => {
            return watchQuery({
              query: PolicyTabsDocument,
              variables: {
                policyId: Number(params.policyId),
              },
            }).map(({ data }) => {
              return {
                title: data?.payroll.policy?.name || i18n.t('payroll_bundle.processing.title'),
                subNavigation: [
                  {
                    title: i18n.t('payroll_bundle.processing.title'),
                    href: navigation.payrollBundle.policy.fullPath({
                      policyId: Number(params.policyId),
                    }),
                    isVisible: data?.payroll._policies.sidebarSeePayrollBundleCycleSubLevelItem.can,
                  },
                  {
                    title: i18n.t('payroll_bundle.updates.title'),
                    href: navigation.payrollBundle.incidences.fullPath(),
                    isVisible:
                      data?.payroll._policies.sidebarSeePayrollBundleIncidencesSubLevelItem.can,
                  },
                  {
                    title: i18n.t('payroll_bundle.inhouse_activation.title'),
                    href: navigation.payrollBundle.inhouseActivation.path,
                    isVisible:
                      data?.payroll._policies.sidebarSeePayrollBundleInHouseActivationSubLevelItem
                        .can,
                  },
                ],
              }
            })
          },
        },
        children: [
          {
            index: true,
            element: <RedirectCyclesPage />,
          },
          {
            path: navigation.payrollBundle.policy.dashboard.path,
            element: <PolicyPeriodDashboardPage />,
            handle: {
              meta: () => ({
                title: i18n.t('payroll_bundle.processing.title'),
              }),
            },
          },
          {
            path: navigation.payrollBundle.policy.supplements.path,
            element: <SupplementsPage />,
          },
          {
            path: navigation.payrollBundle.policy.period.path,
            handle: {
              meta: () => ({
                title: i18n.t('payroll_bundle.processing.title'),
              }),
            },
            children: [
              {
                index: true,
                element: <RedirectCyclesPage />,
              },
              {
                path: navigation.payrollBundle.policy.period.cycles.path,
                element: <CyclesPage />,
                children: [
                  {
                    path: navigation.payrollBundle.policy.period.cycles.results.path,
                    element: <ResultsPage />,
                    handle: {
                      meta: () => ({
                        title: i18n.t('payroll_bundle.cycles_results.title'),
                      }),
                    },
                  },
                  {
                    path: navigation.payrollBundle.policy.period.cycles.supplements.path,
                    element: <SupplementsPage />,
                    handle: {
                      meta: () => ({
                        title: i18n.t('payroll_bundle.cycles_supplements.title'),
                      }),
                    },
                    children: [
                      {
                        path: navigation.payrollBundle.policy.period.cycles.supplements.import.path,
                        element: <BulkImportPage />,
                        handle: {
                          resource: true,
                          meta: () => ({
                            title: i18n.t('payroll_bundle.payroll_breadcrumb.supplements_import'),
                            headerComponent: 'auto',
                          }),
                        },
                      },
                    ],
                  },
                  {
                    path: navigation.payrollBundle.policy.period.cycles.payments.path,
                    element: <PaymentsPage />,
                    handle: {
                      meta: () => ({
                        title: i18n.t('payroll_bundle.cycles_payments.title'),
                      }),
                    },
                    children: [
                      {
                        path: navigation.payrollBundle.policy.period.cycles.payments.generate.path,
                        element: <GeneratePaymentsPage />,
                        handle: {
                          meta: () => ({
                            title: i18n.t('payroll_bundle.generate_payroll_payment.title'),
                          }),
                        },
                      },
                    ],
                  },
                ],
              },
              {
                path: navigation.payrollBundle.policy.period.offcycles.path,
                element: <OffCyclesPage />,
                children: [
                  {
                    path: navigation.payrollBundle.policy.period.offcycles.results.path,
                    element: <ResultsPage />,
                    handle: {
                      meta: () => ({
                        title: i18n.t('payroll_bundle.payroll_breadcrumb.results'),
                      }),
                    },
                  },
                  {
                    path: navigation.payrollBundle.policy.period.offcycles.payments.path,
                    element: <PaymentsPage />,
                    handle: {
                      meta: () => ({
                        title: i18n.t('payroll_bundle.payroll_breadcrumb.payments'),
                      }),
                    },
                    children: [
                      {
                        path: navigation.payrollBundle.policy.period.offcycles.payments.generate
                          .path,
                        element: <GeneratePaymentsPage />,
                        handle: {
                          meta: () => ({
                            title: i18n.t('payroll_bundle.generate_payroll_payment.title'),
                          }),
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: navigation.payrollBundle.policy.issues.path,
            element: <PayrollIssuesNewLayoutPage />,
            handle: {
              meta: () => ({
                title: i18n.t('payroll_bundle.payroll_issues.title'),
                description: i18n.t('payroll_bundle.payroll_issues.description'),
              }),
            },
          },
        ],
      },
    ],
  },
]
